import { Box, Icon, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  PLACEHOLDER_COLOR,
  TEACHER_COLOR,
  TOPIC_COLOR,
  TOPIC_ILLUSTRATIONS
} from "@outschool/ui-components-classroom";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import { pxToRem, useIsLarge, useIsMobile } from "@outschool/ui-utils";
import _ from "lodash";
import React, { PropsWithChildren, useEffect, useRef } from "react";
import { Trans } from "react-i18next";

type SearchSuggestion = {
  uid: string;
  name?: string;
  type: "popular" | "keyword" | "topic" | "teacher" | "placeholder";
};

//@ts-expect-error - TS is whining for some reason
const memoizeObject = _.memoize(_.identity, x => JSON.stringify(x));

function TopicIcon({
  topicUid
}: {
  topicUid: keyof typeof TOPIC_ILLUSTRATIONS;
}) {
  const icon = TOPIC_ILLUSTRATIONS[topicUid]?.smallIcon;
  return icon ? (
    <Icon
      fixedWidth
      icon={icon}
      sx={{
        fontSize: "24px"
      }}
    />
  ) : null;
}

function useTrackSuggestions(
  track,
  keyword,
  searchSuggestions,
  isOpen,
  experiments
) {
  const suggestionTypes =
    Object.keys(searchSuggestions).length > 1
      ? _.countBy(searchSuggestions, s => s.type)
      : null;
  const trackedSuggestions = memoizeObject(suggestionTypes);

  // don't want to track when keyword changes, just want to track what keyword is
  // when suggestions change
  const keywordRef = useRef(keyword);
  keywordRef.current = keyword;

  useEffect(() => {
    if (track && isOpen && trackedSuggestions) {
      track(
        "listings_typeahead_show",
        {
          suggestions: trackedSuggestions,
          keyword: keywordRef.current,
          experiments
        },
        {
          // Redshift only options
          integrations: {
            All: false
          }
        }
      );
    }
  }, [track, isOpen, trackedSuggestions, keywordRef, experiments]);
}

export function SearchSuggestionsMenu({
  getMenuProps,
  isOpen,
  searchSuggestions,
  loadingSuggestions,
  highlightedIndex,
  getItemProps,
  keyword,
  track,
  menuStyle = {},
  activeExperiments = {},
  useGlobalNavUi = false
}: {
  getMenuProps: (props?: unknown) => object;
  isOpen: boolean;
  searchSuggestions: SearchSuggestion[];
  loadingSuggestions: boolean;
  highlightedIndex: number;
  getItemProps: (props?: unknown) => object;
  keyword: string;
  menuStyle?: any;
  track?: (...args: any[]) => void;
  activeExperiments?: any;
  useGlobalNavUi?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\find-classes\\SearchSuggestionsMenu"
  );
  const isMobile = useIsMobile();
  const isLarge = useIsLarge();

  useTrackSuggestions(
    track,
    keyword,
    searchSuggestions,
    isOpen,
    activeExperiments
  );

  searchSuggestions = isMobile
    ? searchSuggestions.slice(0, 6)
    : searchSuggestions;

  const showGlobalNavUiHeader =
    useGlobalNavUi &&
    !loadingSuggestions &&
    searchSuggestions[0]?.type == "placeholder";

  return (
    <ul
      {...getMenuProps({
        style: {
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.25)",
          borderRadius: 8,
          width: isMobile ? 291 : isLarge ? 495 : 421,
          padding: isOpen && searchSuggestions.length > 0 ? 8 : 0,
          position: "absolute",
          listStyle: "none",
          cursor: "pointer",
          background: "white",
          zIndex: 1,
          display: isOpen ? "block" : "none",
          ...menuStyle
        }
      })}
    >
      {loadingSuggestions && (
        <Box>
          <Loading sx={{ paddingY: "200px" }} />
        </Box>
      )}
      {showGlobalNavUiHeader && (
        <li
          style={{
            display: "flex",
            minHeight: 60,
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            fontSize: 20,
            borderBottom: "1px solid rgba(221, 223, 227, 1)",
            backgroundColor: "initial",
            cursor: "default"
          }}
        >
          <Trans t={t}>
            <Typography
              sx={{
                fontSize: pxToRem(16),
                color: "black",
                fontWeight: "500"
              }}
            >
              Suggested searches
            </Typography>
          </Trans>
        </li>
      )}
      {!loadingSuggestions &&
        searchSuggestions.map((suggestion, index) => (
          <TopicSuggestionListItem
            index={index}
            key={`${suggestion.type}-${suggestion.uid}`}
            searchSuggestions={searchSuggestions}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            suggestion={suggestion}
          >
            {suggestion.type === "keyword" && (
              <Typography variant="inherit">
                <Trans t={t}>
                  <Typography
                    variant="inherit"
                    sx={{
                      fontWeight: 500
                    }}
                  >
                    "{{ keyword }}"
                  </Typography>{" "}
                  in all classes
                </Trans>
              </Typography>
            )}
            {(suggestion.type === "topic" || suggestion.type === "popular") && (
              <TopicSearchSuggestion topic={suggestion} />
            )}
            {suggestion.type === "placeholder" && (
              <PlaceholderSearchSuggestion topic={suggestion} />
            )}
            {suggestion.type === "teacher" && (
              <TeacherSearchSuggestion teacher={suggestion} />
            )}
            <SearchSuggestionType suggestion={suggestion} />
          </TopicSuggestionListItem>
        ))}
    </ul>
  );
}

function PlaceholderSearchSuggestion({ topic }) {
  return (
    <Typography
      sx={{
        fontSize: pxToRem(16),
        fontWeight: 500,
        color: PLACEHOLDER_COLOR
      }}
    >
      {topic.name}
    </Typography>
  );
}

function TopicSearchSuggestion({ topic }) {
  return (
    <Typography
      variant="inherit"
      sx={{
        fontWeight: 500,
        color: TOPIC_COLOR
      }}
    >
      <TopicIcon topicUid={topic.uid} />
      <Typography variant="inherit">{topic.name}</Typography>
    </Typography>
  );
}

function TopicSuggestionListItem({
  index,
  searchSuggestions,
  highlightedIndex,
  getItemProps,
  suggestion,
  children
}: PropsWithChildren<{
  index: number;
  searchSuggestions: SearchSuggestion[];
  highlightedIndex: number;
  getItemProps: (props: unknown) => unknown;
  suggestion: SearchSuggestion;
}>) {
  return (
    <li
      style={{
        display: "flex",
        minHeight: 60,
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
        fontSize: 20,
        borderBottom:
          index + 1 !== searchSuggestions.length
            ? "1px solid rgba(221, 223, 227, 1)"
            : "initial",
        backgroundColor: highlightedIndex === index ? "#E6E9FF" : "initial"
      }}
      {...(getItemProps({ item: suggestion, index }) as object)}
    >
      {children}
    </li>
  );
}

function TeacherSearchSuggestion({ teacher }) {
  return (
    <Typography
      variant="inherit"
      sx={{
        fontWeight: 500,
        alignItems: "left",
        color: TEACHER_COLOR,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "flex"
      }}
    >
      <UserHeadshotImage
        user={teacher}
        size={32}
        sx={{ marginRight: "10px" }}
      />
      {teacher.name}
    </Typography>
  );
}

function SearchSuggestionType({ suggestion }) {
  if (suggestion.type == "placeholder") {
    return null;
  }
  return (
    <Typography
      variant="inherit"
      sx={{
        fontSize: 16,
        lineHeight: "24px"
      }}
    >
      {suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1)}
    </Typography>
  );
}
